import { createSignal, Show, For, onMount } from 'solid-js'
import { useNavigate } from '@solidjs/router'
import { useAuth } from '../lib/auth'
import { getCheckIns } from '../lib/api'

export default function History() {
  const navigate = useNavigate()
  const auth = useAuth()

  const [checkIns, setCheckIns] = createSignal<Array<{
    id: string
    venueName: string
    items: Array<{ type: string; tagNumber: number; status: string }>
    totalAmount: number
    status: string
    createdAt: string
    checkedOutAt: string | null
  }>>([])
  const [isLoading, setIsLoading] = createSignal(true)
  const [showAll, setShowAll] = createSignal(false)

  onMount(async () => {
    if (!auth.isAuthenticated()) {
      navigate('/signup')
      return
    }
    await loadHistory()
  })

  async function loadHistory() {
    setIsLoading(true)
    const res = await getCheckIns()
    if (res.data) {
      setCheckIns(res.data.checkIns)
    }
    setIsLoading(false)
  }

  const displayedCheckIns = () =>
    showAll() ? checkIns() : checkIns().slice(0, 5)

  return (
    <div class="min-h-dvh bg-gray-50 flex flex-col">
      {/* Header */}
      <header class="bg-white border-b border-gray-200 px-4 py-3">
        <div class="max-w-md mx-auto flex items-center justify-between">
          <div class="flex items-center gap-2">
            <img src="/logo.svg" alt="ZipCheck" class="h-6" />
            <span class="font-semibold text-gray-900">History</span>
          </div>
          <button
            onClick={() => navigate('/pass')}
            class="text-sm text-emerald-600 hover:text-emerald-700"
          >
            ← Back
          </button>
        </div>
      </header>

      <main class="flex-1 p-4">
        <div class="max-w-md mx-auto">
          <Show
            when={!isLoading()}
            fallback={
              <div class="text-center py-12 text-gray-500">Loading...</div>
            }
          >
            <Show
              when={checkIns().length > 0}
              fallback={
                <div class="text-center py-12">
                  <p class="text-gray-500 mb-2">No check-ins yet</p>
                  <p class="text-sm text-gray-400">
                    Your check-in history will appear here.
                  </p>
                </div>
              }
            >
              <div class="space-y-3">
                <For each={displayedCheckIns()}>
                  {(checkIn) => (
                    <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                      <div class="flex items-center justify-between mb-2">
                        <h3 class="text-sm font-semibold text-gray-900">
                          {checkIn.venueName}
                        </h3>
                        <span
                          class={`text-xs px-2 py-0.5 rounded-full ${
                            checkIn.status === 'active'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {checkIn.status === 'active' ? 'Active' : 'Returned'}
                        </span>
                      </div>

                      <div class="space-y-1 mb-2">
                        <For each={checkIn.items}>
                          {(item) => (
                            <div class="flex items-center justify-between text-sm">
                              <span class="text-gray-600">
                                {item.type} #{item.tagNumber}
                              </span>
                              <span
                                class={`text-xs ${
                                  item.status === 'checked_in'
                                    ? 'text-emerald-600'
                                    : 'text-gray-400'
                                }`}
                              >
                                {item.status === 'checked_in' ? 'Checked in' : 'Returned'}
                              </span>
                            </div>
                          )}
                        </For>
                      </div>

                      <div class="flex items-center justify-between text-xs text-gray-400">
                        <span>
                          {new Date(checkIn.createdAt).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                        <span class="font-medium text-gray-600">
                          €{(checkIn.totalAmount / 100).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}
                </For>
              </div>

              <Show when={checkIns().length > 5 && !showAll()}>
                <button
                  onClick={() => setShowAll(true)}
                  class="mt-4 w-full py-2 px-4 bg-white text-gray-700 font-medium rounded-lg
                         border border-gray-300 hover:bg-gray-50 transition-colors text-sm"
                >
                  Show all ({checkIns().length})
                </button>
              </Show>
            </Show>
          </Show>
        </div>
      </main>
    </div>
  )
}
